// src/pages/CustomerList.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllCustomers, addCustomer, updateCustomer, deleteCustomer } from "../firebase/database";

export default function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({ name: "", city: "", mobile: "", email: "", reference: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await getAllCustomers();
      setCustomers(data);
    } catch (err) {
      console.error("Error loading customers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", city: "", mobile: "", email: "", reference: "" });
    setEditingCustomer(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);
      } else {
        await addCustomer(formData);
      }
      resetForm();
      loadCustomers();
    } catch (err) {
      console.error("Error saving customer:", err);
      alert("Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name || "",
      city: customer.city || "",
      mobile: customer.mobile || "",
      email: customer.email || "",
      reference: customer.reference || "",
    });
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleDelete = async (customerId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete "${customerName}"?`)) return;
    try {
      await deleteCustomer(customerId);
      setCustomers(customers.filter(c => c.id !== customerId));
    } catch (err) {
      console.error("Error deleting customer:", err);
      alert("Failed to delete customer");
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase()) ||
    c.mobile?.includes(search)
  );

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      <style>{`
        .customer-header { display: flex; justify-content: space-between; align-items: center; marginBottom: 24px; gap: 16px; }
        .customer-table-container { background: white; border-radius: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .customer-mobile-cards { display: none; flex-direction: column; gap: 12px; }
        .customer-card { background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); }
        .customer-card-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #F3F4F6; padding-bottom: 10px; }
        .customer-card-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; }
        .info-label { color: #6B7280; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        .customer-card-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 10px; border-top: 1px solid #F3F4F6; }

        @media (max-width: 768px) {
          .customer-header { flex-direction: column; align-items: flex-start; }
          .customer-header > button { width: 100%; }
          .customer-table-container { display: none; }
          .customer-mobile-cards { display: flex; }
          .search-input { width: 100% !important; max-width: none !important; }
        }
      `}</style>

      {/* Header */}
      <div className="customer-header" style={{ marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link 
              to="/admin" 
              style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
              Customer List
            </h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px", fontFamily: "DM Sans, sans-serif" }}>
            Manage customers for invoices
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: "12px 24px",
            background: "#3B82F6",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "DM Sans, sans-serif",
            cursor: "pointer",
          }}
        >
          + Add Customer
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, city, or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            maxWidth: 400,
            padding: "10px 16px",
            border: "1px solid #D1D5DB",
            borderRadius: 8,
            fontSize: 14,
            fontFamily: "DM Sans, sans-serif",
            outline: "none",
          }}
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: 16
        }}>
          <div style={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: "100%",
            maxWidth: 500,
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
            maxHeight: "90vh",
            overflowY: "auto"
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, fontFamily: "DM Sans, sans-serif" }}>
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 480 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Customer name"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                  />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 480 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                    Mobile
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile number"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email address"
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: "DM Sans, sans-serif" }}>
                  Reference
                </label>
                <input
                  type="text"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  placeholder="Referred by"
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    background: saving ? "#9CA3AF" : "#3B82F6",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving..." : (editingCustomer ? "Update" : "Add Customer")}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: "12px 24px",
                    background: "white",
                    color: "#6B7280",
                    border: "1px solid #D1D5DB",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="customer-table-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>NAME</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>CITY</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>MOBILE</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>EMAIL</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>REFERENCE</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
                  No customers found. Click "Add Customer" to create one.
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer, index) => (
                <tr key={customer.id} style={{ borderBottom: index < filteredCustomers.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                  <td style={{ padding: "14px 20px", fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>{customer.name}</td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>{customer.city || "-"}</td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>{customer.mobile || "-"}</td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>{customer.email || "-"}</td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>{customer.reference || "-"}</td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <button
                      onClick={() => handleEdit(customer)}
                      style={{ padding: "6px 12px", fontSize: 12, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", marginRight: 8, fontFamily: "DM Sans, sans-serif" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(customer.id, customer.name)}
                      style={{ padding: "6px 12px", fontSize: 12, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="customer-mobile-cards">
        {filteredCustomers.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif", background: "white", borderRadius: 12 }}>
            No customers found.
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <div key={customer.id} className="customer-card">
              <div className="customer-card-header">
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1F2937" }}>{customer.name}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{customer.city || "No City"}</div>
              </div>
              <div className="customer-card-info">
                <div>
                  <div className="info-label">Mobile</div>
                  <div style={{ fontWeight: 500 }}>{customer.mobile || "—"}</div>
                </div>
                <div>
                  <div className="info-label">Email</div>
                  <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>{customer.email || "—"}</div>
                </div>
                <div>
                  <div className="info-label">Reference</div>
                  <div style={{ fontWeight: 500 }}>{customer.reference || "—"}</div>
                </div>
              </div>
              <div className="customer-card-actions">
                <button
                  onClick={() => handleEdit(customer)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(customer.id, customer.name)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}